/**
 * Units' cut-out shadows at the Medium tier (lifted from the shared prototype
 * engine's `engine/src/character-shadows.js`: the actor's cut-out projected
 * along the key light onto the walkable ground, so architecture keeps its
 * cached shadow map while these move with the units). One instanced draw: a
 * light-facing card per standing unit, sheared onto the ground plane (or the
 * boss's dais) along the key light's direction. The proxies' card is a
 * rectangle of their size; slice 3's sprite planes bring their alpha map. The
 * Off tier keeps slice 1's blob discs.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { addShaderHook } from './material-hooks.ts';
import type { UnitPose } from './units.ts';

export type CharacterShadows = {
  readonly mesh: THREE.InstancedMesh;
  /** Places one shadow per pose; `lightDirection` points from the light toward the ground. */
  update(poses: readonly UnitPose[], visible: (p: UnitPose) => boolean, elevation: (p: UnitPose) => number, lightDirection: THREE.Vector3): void;
  /** Frees the GPU copies (tier Off); the next Medium frame uploads them again. */
  release(): void;
  dispose(): void;
};

const c = presentation.medium.characterShadow;
const u = presentation.unit;

export const createCharacterShadows = (scene: THREE.Scene): CharacterShadows => {
  const geometry = new THREE.PlaneGeometry(1, 1).translate(0, 0.5, 0);
  const material = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: c.opacity, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
  let mesh = new THREE.InstancedMesh(geometry, material, u.initialCapacity);
  mesh.name = 'medium-character-shadows';
  mesh.frustumCulled = false;
  scene.add(mesh);
  const shear = new THREE.Matrix4();
  const card = new THREE.Matrix4();
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  return {
    get mesh() {
      return mesh;
    },
    update(poses, visible, elevation, lightDirection) {
      if (poses.length > mesh.instanceMatrix.count) {
        mesh.removeFromParent();
        mesh.dispose();
        mesh = new THREE.InstancedMesh(geometry, material, poses.length * 2);
        mesh.name = 'medium-character-shadows';
        mesh.frustumCulled = false;
        scene.add(mesh);
      }
      // The card faces the light across the ground (the Rendering page: a camera-facing plane casts a thin shadow).
      const yaw = Math.atan2(lightDirection.x, lightDirection.z);
      const dx = lightDirection.x / -lightDirection.y;
      const dz = lightDirection.z / -lightDirection.y;
      poses.forEach((p, i) => {
        if (!visible(p) || p.fall > c.hideFall || p.gone) {
          mesh.setMatrixAt(i, zero);
          return;
        }
        const big = p.boss ? u.bossScale : 1;
        const body = p.small ? u.smallBody : u.body;
        const head = p.small ? u.smallHead : u.head;
        const g = elevation(p) + c.lift;
        // Onto the plane y = g along the light: x' = x + dx (y - g), y' = g, z' = z + dz (y - g).
        shear.set(1, dx, 0, -dx * g, 0, 0, 0, g, 0, dz, 1, -dz * g, 0, 0, 0, 1);
        q.setFromAxisAngle(up, yaw);
        card.compose(new THREE.Vector3(p.x + p.lungeX + p.swayX, elevation(p), -(p.y + p.lungeY + p.swayY)), q, new THREE.Vector3(body.width * big, (head.center + head.height / 2) * big, 1));
        mesh.setMatrixAt(i, shear.clone().multiply(card));
      });
      mesh.count = poses.length;
      mesh.instanceMatrix.needsUpdate = true;
    },
    release() {
      mesh.dispose();
      geometry.dispose();
    },
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
};

/** One character's cut-out for a projected shadow (slice 6a; `UnitLayer.cutout`), where its feet stand, in world units. */
export type ProjectedActor = {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly cutout: { readonly width: number; readonly height: number; readonly pivotX: number; readonly pivotY: number; readonly rect: readonly [number, number, number, number]; readonly flip: boolean; readonly texture: THREE.Texture | null };
};

export type ProjectedShadows = {
  /** Projects each actor's cut-out (in the camera's rotation, as its sprite stands) along `lightDirection` onto `groundAt`; `opacity` 0 hides them. */
  update(actors: readonly ProjectedActor[], rotation: THREE.Quaternion, lightDirection: THREE.Vector3, groundAt: (x: number, z: number) => number, opacity: number): void;
  /** Frees every projection and the blank map's GPU copy (Off draws none). */
  release(): void;
  dispose(): void;
};

/**
 * Characters' sun shadows in a scene (slice 6a; the shared engine's
 * `character-shadows.js` as lantern-water used it): each actor's animated
 * cut-out, a subdivided plane, is projected vertex by vertex along the sun
 * onto the walkable height field (a march down the ray, then a bisection), so
 * the shadow follows stairs and bridge curves while the architecture keeps
 * its cached shadow map. A triangle that would stretch across a drop larger
 * than a stair riser collapses. `discard` is GLSL over `shadowGround` (world
 * x and z) that drops the shadow where nothing receives it (the open canal).
 * A projection is recomputed only when its actor moved or changed frame.
 */
export const createProjectedShadows = (scene: THREE.Scene, discard: string, look: { readonly opacity: number; readonly march: number; readonly refine: number; readonly split: number; readonly lift: number; readonly segments: readonly [number, number] }): ProjectedShadows => {
  type Projected = { readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>; readonly source: Float32Array; readonly sourceIndex: readonly number[]; signature: string };
  const records = new Map<string, Projected>();
  const blank = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
  blank.needsUpdate = true;
  const point = new THREE.Vector3();
  const matrix = new THREE.Matrix4();
  const local = new THREE.Matrix4();
  const at = new THREE.Vector3();
  const one = new THREE.Vector3(1, 1, 1);
  const free = (r: Projected): void => {
    r.mesh.removeFromParent();
    r.mesh.geometry.dispose();
    r.mesh.material.dispose();
  };
  const make = (): Projected => {
    const geometry = new THREE.PlaneGeometry(1, 1, look.segments[0], look.segments[1]).translate(0.5, 0.5, 0);
    const material = new THREE.MeshBasicMaterial({ map: blank, color: 0x000000, fog: false, transparent: true, opacity: look.opacity, alphaTest: 0.05, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    addShaderHook(material, {
      key: `projected-shadow:${discard}`,
      vertexHeader: 'varying vec2 shadowGround;',
      fragmentHeader: 'varying vec2 shadowGround;',
      vertex: [{ chunk: 'begin_vertex', after: 'shadowGround=position.xz;' }],
      fragment: [{ chunk: 'map_fragment', after: `${discard}\ndiffuseColor.rgb=diffuse;` }],
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.name = 'projected-shadow';
    mesh.userData['dynamic'] = true;
    scene.add(mesh);
    const index = geometry.getIndex();
    return { mesh, source: Float32Array.from(geometry.getAttribute('position').array), sourceIndex: index === null ? [] : Array.from(index.array), signature: '' };
  };
  return {
    update(actors, rotation, lightDirection, groundAt, opacity) {
      const direction = lightDirection.clone().divideScalar(-lightDirection.y);
      const live = new Set(actors.map((a) => a.id));
      for (const [id, r] of records) {
        if (live.has(id)) continue;
        free(r);
        records.delete(id);
      }
      for (const a of actors) {
        let r = records.get(a.id);
        if (r === undefined) {
          r = make();
          records.set(a.id, r);
        }
        r.mesh.visible = opacity > 0.02;
        if (!r.mesh.visible) continue;
        const c = a.cutout;
        r.mesh.material.opacity = opacity;
        const map = c.texture ?? blank;
        if (r.mesh.material.map !== map) {
          r.mesh.material.map = map;
          r.mesh.material.needsUpdate = true;
        }
        const signature = [a.x, a.y, a.z, ...c.rect, c.flip ? 1 : 0, c.width, c.height, rotation.x, rotation.y, rotation.z, rotation.w].map((v) => v.toFixed(4)).join(',');
        if (signature === r.signature) continue;
        r.signature = signature;
        matrix.compose(at.set(a.x, a.y, a.z), rotation, one).multiply(local.makeTranslation(-c.pivotX, -c.pivotY, 0)).multiply(local.makeScale(c.width, c.height, 1));
        const geometry = r.mesh.geometry;
        const positions = geometry.getAttribute('position');
        const uv = geometry.getAttribute('uv');
        for (let i = 0; i < positions.count; i += 1) {
          const u = r.source[i * 3] ?? 0;
          const v = r.source[i * 3 + 1] ?? 0;
          uv.setXY(i, c.rect[0] + (c.flip ? 1 - u : u) * c.rect[2], c.rect[1] + (1 - v) * c.rect[3]);
          point.fromArray(r.source, i * 3).applyMatrix4(matrix);
          let low = 0;
          let high = 0;
          const fall = Math.max(0.1, point.y + 0.1);
          for (let step = 1; step <= look.march; step += 1) {
            high = (fall * step) / look.march;
            if (point.y - high <= groundAt(point.x + direction.x * high, point.z + direction.z * high)) break;
            low = high;
          }
          for (let step = 0; step < look.refine; step += 1) {
            const mid = (low + high) * 0.5;
            if (point.y - mid > groundAt(point.x + direction.x * mid, point.z + direction.z * mid)) low = mid;
            else high = mid;
          }
          const x = point.x + direction.x * high;
          const z = point.z + direction.z * high;
          positions.setXYZ(i, x, groundAt(x, z) + look.lift, z);
        }
        positions.needsUpdate = true;
        uv.needsUpdate = true;
        const index = geometry.getIndex();
        if (index !== null) {
          for (let i = 0; i < r.sourceIndex.length; i += 3) {
            const ia = r.sourceIndex[i] ?? 0;
            const ib = r.sourceIndex[i + 1] ?? 0;
            const ic = r.sourceIndex[i + 2] ?? 0;
            const ya = positions.getY(ia);
            const yb = positions.getY(ib);
            const yc = positions.getY(ic);
            const split = Math.max(ya, yb, yc) - Math.min(ya, yb, yc) > look.split;
            index.setX(i, ia);
            index.setX(i + 1, split ? ia : ib);
            index.setX(i + 2, split ? ia : ic);
          }
          index.needsUpdate = true;
        }
      }
    },
    release() {
      for (const r of records.values()) free(r);
      records.clear();
      blank.dispose();
    },
    dispose() {
      for (const r of records.values()) free(r);
      records.clear();
      blank.dispose();
    },
  };
};
